import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, DollarSign, FileText, Users, Settings, Sparkles,
  Check, Loader2, Plus, ChevronRight, Copy, MapPin, Shield,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * TenantBillingTab — billing config, dynamic pricing, feature add-ons
 */
export function TenantBillingTab({ token, tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [features, setFeatures] = useState({});
  const [tab, setTab] = useState('billing'); // billing, features
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const [billingRes, tenantRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/tenants/${tenantId}/billing`, { headers }),
        axios.get(`${BACKEND_URL}/api/tenants`, { headers }),
      ]);
      setData(billingRes.data);
      setForm(billingRes.data.billing || {});
      const tenants = tenantRes.data.tenants || tenantRes.data || [];
      const t = tenants.find(t => t.id === tenantId);
      setFeatures(t?.features || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [tenantId, headers]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const saveBilling = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${tenantId}/billing`, form, { headers });
      toast.success('Billing updated');
      fetchBilling();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveFeatures = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${tenantId}/features`, features, { headers });
      toast.success('Features updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const ALL_FEATURES = [
    { key: 'stack_map', label: 'Stack Map' }, { key: 'tech_database', label: 'Tech Database' },
    { key: 'basic_reports', label: 'Basic Reports' }, { key: 'advanced_reports', label: 'Advanced Reports' },
    { key: 'executive_reports', label: 'Executive Reports' }, { key: 'risk_reports', label: 'Risk Reports' },
    { key: 'property_dashboard', label: 'Property Dashboard' }, { key: 'governance', label: 'Governance' },
    { key: 'compliance', label: 'Compliance' }, { key: 'contracts', label: 'Contracts' },
    { key: 'corporate_stack', label: 'Corporate Stack' }, { key: 'rfp_management', label: 'RFP Management' },
    { key: 'benchmarking', label: 'Benchmarking' }, { key: 'vendor_portal', label: 'Vendor Portal' },
    { key: 'brand_portal', label: 'Brand Portal' }, { key: 'export_pdf', label: 'PDF Export' },
    { key: 'export_csv', label: 'CSV Export' }, { key: 'export_email', label: 'Email Reports' },
    { key: 'financial_hub', label: 'Financial Hub' }, { key: 'ai_features', label: 'AI Features' },
    { key: 'tools', label: 'Tools Hub' }, { key: 'white_label', label: 'White Label' },
  ];

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const calc = data?.calculated || {};

  return (
    <ScrollArea className="h-full">
      <div className="p-5 max-w-4xl space-y-6">
        {/* Tab switcher: Billing | Features */}
        <div className="flex gap-1 bg-muted/60 rounded-lg p-0.5 w-fit">
          <button onClick={() => setTab('billing')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'billing' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>Billing & Contract</button>
          <button onClick={() => setTab('features')} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'features' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>Features On/Off</button>
        </div>

        {tab === 'features' ? (
          <div className="border rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold">Feature Toggles</h3>
            <p className="text-xs text-muted-foreground">Enable or disable features for this tenant. Changes take effect immediately.</p>
            <div className="grid grid-cols-2 gap-1">
              {ALL_FEATURES.map(f => (
                <label key={f.key} className="flex items-center gap-2 p-1.5 rounded-md text-xs hover:bg-muted/40 cursor-pointer">
                  <input type="checkbox" checked={!!features[f.key]}
                    onChange={e => setFeatures(prev => ({ ...prev, [f.key]: e.target.checked }))}
                    className="rounded h-3.5 w-3.5" />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <Button onClick={saveFeatures} disabled={saving} className="w-full gap-1.5">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save Features
            </Button>
          </div>
        ) : (
        <>
        {/* Dynamic cost summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="border rounded-xl p-3 bg-card text-center">
            <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>${calc.monthly_total?.toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground">Monthly Total</div>
          </div>
          <div className="border rounded-xl p-3 bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${calc.annual_total?.toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground">Annual Total</div>
          </div>
          <div className="border rounded-xl p-3 bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{calc.total_rooms}</div>
            <div className="text-[10px] text-muted-foreground">Total Rooms</div>
          </div>
          <div className="border rounded-xl p-3 bg-card text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{calc.property_count}</div>
            <div className="text-[10px] text-muted-foreground">Properties</div>
          </div>
        </div>

        {/* Pricing Model */}
        <div className="border rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold">Pricing Model</h3>
          <div className="grid grid-cols-3 gap-2">
            {[{v:'prpm',l:'PRPM (Per Room)'},{v:'flat',l:'Flat Rate'},{v:'hybrid',l:'Hybrid'}].map(m => (
              <button key={m.v} onClick={() => setForm(f => ({...f, pricing_model: m.v}))}
                className={`p-3 rounded-lg border text-xs font-medium text-center transition-colors ${form.pricing_model === m.v ? 'bg-primary/10 border-primary ring-1 ring-primary' : 'hover:bg-muted/60'}`}>
                {m.l}
              </button>
            ))}
          </div>
          {form.pricing_model === 'prpm' && (
            <div><Label className="text-xs">Rate ($/room/month)</Label><Input type="number" step="0.01" value={form.prpm_rate || ''} onChange={e => setForm(f => ({...f, prpm_rate: parseFloat(e.target.value) || 0}))} /></div>
          )}
          {form.pricing_model === 'flat' && (
            <div><Label className="text-xs">Flat Rate ($/month)</Label><Input type="number" step="1" value={form.flat_rate || ''} onChange={e => setForm(f => ({...f, flat_rate: parseFloat(e.target.value) || 0}))} /></div>
          )}
          {form.pricing_model === 'hybrid' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Base ($/month)</Label><Input type="number" value={form.hybrid_base || ''} onChange={e => setForm(f => ({...f, hybrid_base: parseFloat(e.target.value) || 0}))} /></div>
              <div><Label className="text-xs">+ PRPM ($/room/month)</Label><Input type="number" step="0.01" value={form.hybrid_prpm || ''} onChange={e => setForm(f => ({...f, hybrid_prpm: parseFloat(e.target.value) || 0}))} /></div>
            </div>
          )}
        </div>

        {/* Contract */}
        <div className="border rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold">Contract</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start</Label><Input type="date" value={form.contract_start || ''} onChange={e => setForm(f => ({...f, contract_start: e.target.value}))} /></div>
            <div><Label className="text-xs">End</Label><Input type="date" value={form.contract_end || ''} onChange={e => setForm(f => ({...f, contract_end: e.target.value}))} /></div>
            <div><Label className="text-xs">Payment Terms</Label>
              <Select value={form.payment_terms || ''} onValueChange={v => setForm(f => ({...f, payment_terms: v}))}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{['net-15','net-30','net-60','due-on-receipt'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Billing Frequency</Label>
              <Select value={form.billing_frequency || ''} onValueChange={v => setForm(f => ({...f, billing_frequency: v}))}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{['monthly','quarterly','semi-annual','annual'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Billing Scope</Label>
              <Select value={form.billing_scope || ''} onValueChange={v => setForm(f => ({...f, billing_scope: v}))}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent><SelectItem value="tenant">Per Tenant (1 invoice)</SelectItem><SelectItem value="property">Per Property</SelectItem><SelectItem value="group">Per Group</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Signed By</Label><Input value={form.signed_by || ''} onChange={e => setForm(f => ({...f, signed_by: e.target.value}))} /></div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.auto_renew || false} onChange={e => setForm(f => ({...f, auto_renew: e.target.checked}))} className="rounded" /> Auto-renew</label>
          </div>
        </div>

        {/* Contacts */}
        <div className="border rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold">Contacts</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Account Manager</Label><Input value={form.account_manager || ''} onChange={e => setForm(f => ({...f, account_manager: e.target.value}))} /></div>
            <div><Label className="text-xs">Manager Email</Label><Input value={form.account_manager_email || ''} onChange={e => setForm(f => ({...f, account_manager_email: e.target.value}))} /></div>
            <div><Label className="text-xs">Billing Contact</Label><Input value={form.billing_contact_name || ''} onChange={e => setForm(f => ({...f, billing_contact_name: e.target.value}))} /></div>
            <div><Label className="text-xs">Billing Email</Label><Input value={form.billing_contact_email || ''} onChange={e => setForm(f => ({...f, billing_contact_email: e.target.value}))} /></div>
          </div>
        </div>

        {/* Special Terms */}
        <div className="border rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold">Special Terms & Notes</h3>
          <Textarea value={form.special_terms || ''} onChange={e => setForm(f => ({...f, special_terms: e.target.value}))} rows={2} placeholder="Special pricing terms..." />
          <Textarea value={form.negotiation_notes || ''} onChange={e => setForm(f => ({...f, negotiation_notes: e.target.value}))} rows={2} placeholder="Negotiation notes..." />
        </div>

        <Button onClick={saveBilling} disabled={saving} className="w-full gap-1.5">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save Billing Configuration
        </Button>

        {/* Properties with rooms */}
        {(data?.properties || []).length > 0 && (
          <div className="border rounded-xl p-4">
            <h3 className="text-sm font-bold mb-2">Properties ({calc.property_count})</h3>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {data.properties.map(p => (
                <div key={p.id} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/30">
                  <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="font-medium flex-1">{p.name}</span>
                  <span className="tabular-nums">{p.rooms || 0} rooms</span>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </ScrollArea>
  );
}

/**
 * TenantProposalTab — AI proposal/contract generation + negotiation log
 */
export function TenantProposalTab({ token, tenantId, tenantName }) {
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [resultType, setResultType] = useState('');
  const [negotiations, setNegotiations] = useState([]);
  const [noteForm, setNoteForm] = useState({ subject: '', details: '', type: 'note' });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/tenants/${tenantId}/negotiations`, { headers })
      .then(r => setNegotiations(r.data.entries || []))
      .catch(() => {});
  }, [tenantId, headers]);

  const generate = async (type) => {
    setGenerating(true);
    setResultType(type);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/tenants/${tenantId}/generate-${type}`, {}, { headers });
      setResult(res.data);
      toast.success(`${type === 'proposal' ? 'Proposal' : 'Contract'} generated`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const addNote = async () => {
    if (!noteForm.subject.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tenants/${tenantId}/negotiations`, noteForm, { headers });
      toast.success('Note added');
      setNoteForm({ subject: '', details: '', type: 'note' });
      const res = await axios.get(`${BACKEND_URL}/api/admin/tenants/${tenantId}/negotiations`, { headers });
      setNegotiations(res.data.entries || []);
    } catch { toast.error('Failed'); }
  };

  const typeColors = { note: 'bg-blue-100 text-blue-700', proposal_sent: 'bg-purple-100 text-purple-700', counter: 'bg-amber-100 text-amber-700', accepted: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <ScrollArea className="h-full">
      <div className="p-5 max-w-4xl space-y-6">
        {/* Generate buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => generate('proposal')} disabled={generating}>
            {generating && resultType === 'proposal' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5 text-purple-500" />}
            <span className="text-xs">Generate Proposal</span>
          </Button>
          <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => generate('contract')} disabled={generating}>
            {generating && resultType === 'contract' ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5 text-blue-500" />}
            <span className="text-xs">Generate Contract</span>
          </Button>
        </div>

        {/* Generated document */}
        {result && (
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold">{resultType === 'proposal' ? 'Pricing Proposal' : 'Contract'} for {result.tenant_name}</h3>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { navigator.clipboard.writeText(result.proposal || result.contract); toast.success('Copied'); }}>
                <Copy className="h-3 w-3 mr-1" /> Copy
              </Button>
            </div>
            <div className="prose prose-sm max-w-none text-xs whitespace-pre-wrap max-h-[400px] overflow-y-auto bg-muted/20 p-4 rounded-lg">
              {result.proposal || result.contract}
            </div>
          </div>
        )}

        {/* Negotiation log */}
        <div className="border rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold">Negotiation Log</h3>
          <div className="grid grid-cols-3 gap-2">
            <Input value={noteForm.subject} onChange={e => setNoteForm(f => ({...f, subject: e.target.value}))} placeholder="Subject..." className="col-span-2" />
            <Select value={noteForm.type} onValueChange={v => setNoteForm(f => ({...f, type: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[{v:'note',l:'Note'},{v:'proposal_sent',l:'Proposal Sent'},{v:'counter',l:'Counter Offer'},{v:'accepted',l:'Accepted'},{v:'rejected',l:'Rejected'}].map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea value={noteForm.details} onChange={e => setNoteForm(f => ({...f, details: e.target.value}))} rows={2} placeholder="Details..." />
          <Button size="sm" onClick={addNote} disabled={!noteForm.subject.trim()} className="gap-1"><Plus className="h-3 w-3" /> Add Entry</Button>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {negotiations.map(n => (
              <div key={n.id} className="p-2.5 rounded-lg border text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-[8px] ${typeColors[n.type] || ''}`}>{n.type}</Badge>
                  <span className="font-medium flex-1">{n.subject}</span>
                  <span className="text-[9px] text-muted-foreground">{n.date ? new Date(n.date).toLocaleDateString() : ''}</span>
                </div>
                {n.details && <p className="text-muted-foreground">{n.details}</p>}
                <span className="text-[8px] text-muted-foreground">{n.created_by}</span>
              </div>
            ))}
            {negotiations.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No negotiation entries yet.</p>}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
